import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { breakpoints, colors } from '@/styles'

import {
  CityInfoMain,
  CityInfoWrapper,
  Description,
  FaqSection,
  SectionTitle,
  Title,
  Wrapper
} from './elements'

const RentalResourcesWrapper = styled.div`
  display: flex;
  gap: 32px;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
  }
`

const RentalResourceContainer = styled.div`
  flex: 1;

  ul {
    list-style-type: none;
    padding: 0;
    margin: 0;

    li:not(:last-child) {
      margin-bottom: 16px;
    }
  }

  a {
    text-decoration: none;
    color: ${colors.secondary};
    font-size: 14px;
    transition: opacity 0.1s ease-in-out;

    :hover {
      opacity: 0.7;
    }
  }
`

type CompanyData = {
  name: string
  url: string
  about: JSX.Element
  faqs: Record<string, string>[]
}

type CompanyPageContentProps = {
  data: CompanyData
}

const CompanyPageContent: React.FC<CompanyPageContentProps> = ({ data }) => {
  const { t } = useTranslation()

  return (
    <>
      <Wrapper>
        <CityInfoWrapper>
          <CityInfoMain>
            <Title>
              <Trans
                i18nKey="seo:About Company Title"
                values={{ companyName: data.name }}
                defaults="About {{ companyName }}"
              />
            </Title>
            <Description>{data.about}</Description>
          </CityInfoMain>
          <div>
            <Title>
              <Trans
                i18nKey="seo:Company FAQ Title"
                values={{ companyName: data.name }}
                defaults="{{ companyName }} FAQ"
              />
            </Title>
            {data.faqs.map(faq => (
              <FaqSection key={faq.question}>
                <Description>{faq.question}</Description>
                <Description>{faq.answer}</Description>
              </FaqSection>
            ))}
          </div>
          <div>
            <Title>{t('seo:Rental Resources', 'Rental Resources')}</Title>
            <RentalResourcesWrapper>
              <RentalResourceContainer>
                <SectionTitle>Resources For Renters</SectionTitle>
                <ul>
                  <li>
                    <a
                      href="https://liv.rent/blog/2019/07/how-to-protect-yourself-from-rental-scams-in-canada/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      How to avoid rental scams
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://liv.rent/blog/2018/10/tips-for-finding-an-apartment-in-vancouver/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Tips for finding an apartment for rent in Vancouver
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://liv.rent/blog/2019/09/residential-tenancy-agreement-explained/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      BC residential tenancy agreement explained
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://liv.rent/blog/2020/06/finding-a-roommate-during-covid19/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      How to find a roommate in Vancouver
                    </a>
                  </li>
                </ul>
              </RentalResourceContainer>
              <RentalResourceContainer>
                <SectionTitle>Resources For Landlords</SectionTitle>
                <ul>
                  <li>
                    <a
                      href="https://liv.rent/blog/2022/01/top-8-apartment-rental-websites-vancouver/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Top apartment rental websites Vancouver
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://liv.rent/blog/2021/08/questions-every-landlord-should-ask-applicants-before-they-sign-a-lease/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Questions every landlord should ask tenants before signing
                      a lease
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://liv.rent/blog/2019/10/trust-score-explained/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      How to screen tenants with trust score
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://liv.rent/blog/2021/10/what-is-a-lease-addendum/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      What is a lease addendum
                    </a>
                  </li>
                </ul>
              </RentalResourceContainer>
            </RentalResourcesWrapper>
          </div>
        </CityInfoWrapper>
      </Wrapper>
    </>
  )
}

export default CompanyPageContent
